import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Stack,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  List,
  Paper,
  Grid,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getOneAppointment } from '../../apis/appointmentApi';
import { useDispatch, useSelector } from 'react-redux';
import { updateAppointment } from '../../apis/appointmentSlice';

const PaymentDialog = ({ open, handleClose, appointmentId, appointmentDetails, callApi }) => {
  const dispatch = useDispatch();
  const { loggedIn } = useSelector((state) => state.authData || {});
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [appointment, setAppointment] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionIndex, setSelectedSessionIndex] = useState(0);
  const [paidByCustomer, setPaidByCustomer] = useState('');

  const fetchAppointment = async () => {
    let res;
    const targetId = appointmentId || appointmentDetails;
    if (!targetId) return;
    res = await getOneAppointment(targetId);
    const appt = res?.data?.data;
    setAppointment(appt);
    setSessions((appt && appt.sessions) || []);
    setSelectedSessionIndex(0);
    setPaidByCustomer('');
  };

  useEffect(() => {
    if (open) fetchAppointment();
  }, [open, appointmentId, appointmentDetails]);

  const formatDate = (d) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const handleSessionSelect = (e) => {
    setSelectedSessionIndex(Number(e.target.value));
    setPaidByCustomer('');
  };

  const handleChangePaid = (val) => {
    if (val === '') {
      setPaidByCustomer('');
      return;
    }
    const idx = selectedSessionIndex;
    const sess = sessions[idx] || {};
    const remaining = Number(sess.remainingAmount || sess.payment - sess.paidAmount || 0) || 0;
    const num = Math.max(0, Number(val || 0));
    setPaidByCustomer(Math.min(num, remaining));
  };

  const handleSubmit = async () => {
    const idx = selectedSessionIndex;
    if (!appointment) return;
    const updated = [...sessions];
    const sess = { ...(updated[idx] || {}) };
    const prevPaid = Number(sess.paidAmount || 0);
    const payNow = Number(paidByCustomer || 0);
    const newPaid = prevPaid + payNow;
    sess.paidAmount = newPaid;
    sess.remainingAmount = Math.max(0, Number(sess.payment || 0) - newPaid);
    sess.paymentLogs = sess.paymentLogs || [];
    if (payNow > 0) {
      sess.paymentLogs.push({
        paidAmount: payNow,
        receiveBy: (loggedIn && loggedIn._id) || null,
        paymentDate: new Date(),
      });
    }
    updated[idx] = sess;

    // send updated sessions to backend
    const payload = { _id: appointment._id, sessions: updated };
    const response = await dispatch(updateAppointment(payload));
    if (response?.payload?.success) {
      handleClose();
      callApi && callApi();
    }
  };

  const selectedSession = sessions[selectedSessionIndex] || {};

  const totalRemainingAmount = useMemo(() => {
    return (sessions || [])
      .filter((s) => s.isSessionTaken === true) // ignore future sessions
      .reduce((sum, s) => {
        const remaining = s.remainingAmount ?? Math.max(0, (s.payment || 0) - (s.paidAmount || 0));
        return sum + Number(remaining || 0);
      }, 0);
  }, [sessions]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="payment-dialog-title"
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          border: '1px solid var(--color-border-soft)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id="payment-dialog-title"
        sx={{
          py: 1.1,
          px: 1.5,
          fontSize: '1rem',
          fontWeight: 700,
          color: 'var(--color-heading)',
          borderBottom: '1px solid var(--color-border-soft)',
          background: '#f8fbff',
        }}
      >
        Payment For Session
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.1, sm: 1.4 } }}>
        {!appointment && <Typography sx={{ fontSize: '0.82rem' }}>Loading...</Typography>}

        {appointment && (
          <Box>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--color-heading)' }}>
              Patient: {appointment.patient?.name || appointment.patientId?.name}
            </Typography>

            <FormControl
              fullWidth
              sx={{
                mt: 1.2,
                mb: 1.2,
                '& .MuiInputBase-root': { fontSize: '0.84rem', borderRadius: '8px' },
                '& .MuiInputLabel-root': { fontSize: '0.8rem' },
              }}
            >
              <InputLabel id="session-select-label">Select Session</InputLabel>
              <Select
                labelId="session-select-label"
                value={sessions.length ? selectedSessionIndex : ''}
                label="Select Session"
                onChange={handleSessionSelect}
              >
                {!sessions.length && (
                  <MenuItem value="" disabled>
                    No sessions available
                  </MenuItem>
                )}
                {sessions.map((s, i) => (
                  <MenuItem key={i} value={i}>
                    {`Session ${s.sessionNo || i + 1} - ${formatDate(s.sessionDate)} - Remaining ${
                      s.remainingAmount || s.payment - s.paidAmount || 0
                    } - Paid ${s.paidAmount || 0}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Paper
              sx={{
                border: '1px solid var(--color-border-soft)',
                borderRadius: 1.5,
                p: 1,
                mb: 1.25,
                backgroundColor: '#fcfdff',
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-heading)', mb: 0.7 }}>
                Session Payment Details
              </Typography>
              <Grid container spacing={0.7}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.79rem' }}>
                    <strong>Payment:</strong> {selectedSession.payment || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.79rem' }}>
                    <strong>Date:</strong> {formatDate(selectedSession.sessionDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.79rem' }}>
                    <strong>Paid:</strong> {selectedSession.paidAmount || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.79rem' }}>
                    <strong>Remaining:</strong>{' '}
                    {selectedSession.remainingAmount ??
                      Math.max(0, (selectedSession.payment || 0) - (selectedSession.paidAmount || 0))}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-heading)' }}>
                    Total Remaining: {totalRemainingAmount}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Paid By Customer"
                  value={paidByCustomer}
                  type="number"
                  onChange={(e) => handleChangePaid(e.target.value)}
                  sx={{
                    '& .MuiInputBase-root': { fontSize: '0.84rem', borderRadius: '8px' },
                    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Remaining After"
                  value={Math.max(
                    0,
                    (selectedSession.payment || 0) -
                      ((selectedSession.paidAmount || 0) + Number(paidByCustomer || 0)),
                  )}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputBase-root': { fontSize: '0.84rem', borderRadius: '8px' },
                    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 1.25, py: 1, borderTop: '1px solid var(--color-border-soft)', gap: 1 }}>
        <Button
          onClick={() => {
            handleClose();
          }}
          sx={{
            textTransform: 'none',
            minHeight: 32,
            borderRadius: '8px',
            border: '1px solid var(--color-border-soft)',
            color: 'var(--color-heading)',
            background: '#fff',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          sx={{
            textTransform: 'none',
            minHeight: 32,
            borderRadius: '8px',
            backgroundImage: 'linear-gradient(180deg, #4B45FF 0%, #191C63 100%)',
            color: 'white',
            boxShadow: 'none',
          }}
        >
          Pay
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
